import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useCustomerCreditsBalance,
  useCustomerCreditsExpiring,
  useCustomerCreditsHistory,
} from '@/hooks/customer/useCustomerCredits';
import type { CustomerCreditHistoryRecord } from '@/types/customer-credits';

interface CustomerCreditsPageProps {
  showPageHeader?: boolean;
}

type StatusColor = 'success' | 'warning' | 'danger' | 'secondary';

const statusStyles: Record<StatusColor, string> = {
  success: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-100 dark:border-emerald-800',
  warning: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-100 dark:border-amber-800',
  danger: 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-100 dark:border-rose-800',
  secondary: 'bg-muted text-foreground border-border',
};

function resolveStatusColor(status?: string | null, hasExpired?: boolean): StatusColor {
  if (hasExpired) return 'danger';
  switch (status) {
    case 'completed':
    case 'active':
      return 'success';
    case 'pending':
      return 'warning';
    case 'failed':
    case 'canceled':
    case 'refunded':
      return 'danger';
    default:
      return 'secondary';
  }
}

function formatCurrency(language: string, currency: string, amount: number) {
  try {
    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function formatCredits(value?: number | null, language = 'en') {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(language).format(value);
}

function formatDate(value?: string | null, formatter?: Intl.DateTimeFormat) {
  if (!value || !formatter) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return formatter.format(parsed);
}

function resolveAmount(record: CustomerCreditHistoryRecord, language: string) {
  if (record.currency && record.formattedAmount) {
    return `${record.currency} ${record.formattedAmount}`;
  }
  if (record.currency && typeof record.amount === 'number') {
    return formatCurrency(language, record.currency, record.amount / 100);
  }
  return '—';
}

export default function CustomerCreditsPage({ showPageHeader = true }: CustomerCreditsPageProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language ?? i18n.resolvedLanguage ?? 'en';
  const dateFormatter = useMemo(() => new Intl.DateTimeFormat(language, { dateStyle: 'medium' }), [language]);

  const balanceQuery = useCustomerCreditsBalance();
  const historyQuery = useCustomerCreditsHistory();
  const expiringQuery = useCustomerCreditsExpiring(30);

  const isLoading = balanceQuery.isLoading || historyQuery.isLoading || expiringQuery.isLoading;
  const hasError = balanceQuery.isError || historyQuery.isError || expiringQuery.isError;

  const expiringRecords = expiringQuery.data?.records ?? [];
  const expiringDays = expiringQuery.data?.days ?? 30;

  const expiringCredits = expiringRecords.reduce((total, record) => {
    const remaining = record.creditsRemaining ?? record.creditsPurchased ?? 0;
    return total + remaining;
  }, 0);

  const retryAll = () => {
    void balanceQuery.refetch();
    void historyQuery.refetch();
    void expiringQuery.refetch();
  };

  const renderHistoryRows = () => {
    if (historyQuery.isLoading) {
      return Array.from({ length: 3 }).map((_, index) => (
        <TableRow key={`history-skeleton-${index}`}>
          {Array.from({ length: 6 }).map((__, cellIndex) => (
            <TableCell key={`history-skeleton-${index}-${cellIndex}`}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ));
    }

    if (!historyQuery.data?.length) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
            <div className="space-y-2">
              <p className="font-medium text-foreground">{t('customer.billing.credits.historyEmptyTitle')}</p>
              <p className="text-sm">{t('customer.billing.credits.historyEmptyDescription')}</p>
            </div>
          </TableCell>
        </TableRow>
      );
    }

    return historyQuery.data.map((record) => {
      const statusKey = record.status ?? (record.isCompleted ? 'completed' : 'pending');
      const statusLabel = t(`customer.billing.credits.status.${statusKey}`, {
        defaultValue: statusKey?.replace(/_/g, ' ') ?? t('customer.billing.credits.status.unknown'),
      });
      const badgeClass = statusStyles[resolveStatusColor(statusKey, record.hasExpired)];
      return (
        <TableRow key={record.id}>
          <TableCell className="font-medium">
            {record.package?.name ?? t('customer.billing.credits.unknownPackage')}
          </TableCell>
          <TableCell>{resolveAmount(record, language)}</TableCell>
          <TableCell>{formatCredits(record.creditsPurchased, language)}</TableCell>
          <TableCell>{formatCredits(record.creditsRemaining, language)}</TableCell>
          <TableCell>
            <Badge variant="outline" className={cn('capitalize', badgeClass)}>
              {statusLabel}
            </Badge>
          </TableCell>
          <TableCell>{formatDate(record.paidAt ?? record.createdAt, dateFormatter)}</TableCell>
          <TableCell>{formatDate(record.expiresAt, dateFormatter)}</TableCell>
        </TableRow>
      );
    });
  };

  const renderExpiringRows = () => {
    if (expiringQuery.isLoading) {
      return Array.from({ length: 2 }).map((_, index) => (
        <TableRow key={`expiring-skeleton-${index}`}>
          {Array.from({ length: 4 }).map((__, cellIndex) => (
            <TableCell key={`expiring-skeleton-${index}-${cellIndex}`}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ));
    }

    if (!expiringRecords.length) {
      return (
        <TableRow>
          <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
            <div className="space-y-2">
              <p className="font-medium text-foreground">{t('customer.billing.credits.expiringEmptyTitle')}</p>
              <p className="text-sm">
                {t('customer.billing.credits.expiringEmptyDescription', { days: expiringDays })}
              </p>
            </div>
          </TableCell>
        </TableRow>
      );
    }

    return expiringRecords.map((record) => (
      <TableRow key={record.id}>
        <TableCell className="font-medium">
          {record.package?.name ?? t('customer.billing.credits.unknownPackage')}
        </TableCell>
        <TableCell>{formatCredits(record.creditsRemaining ?? record.creditsPurchased, language)}</TableCell>
        <TableCell>{formatDate(record.expiresAt, dateFormatter)}</TableCell>
        <TableCell>{record.daysUntilExpiry ?? '—'}</TableCell>
      </TableRow>
    ));
  };

  return (
    <div className="space-y-6">
      {showPageHeader ? (
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold">{t('customer.billing.credits.title')}</h1>
          <p className="text-muted-foreground">{t('customer.billing.credits.subtitle')}</p>
        </div>
      ) : null}

      {hasError ? (
        <Alert variant="destructive">
          <AlertTitle>{t('customer.billing.credits.loadError')}</AlertTitle>
          <AlertDescription className="mt-2">
            <Button variant="outline" size="sm" className="gap-2" onClick={retryAll}>
              <RefreshCw className="h-4 w-4" />
              {t('customer.billing.credits.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('customer.billing.credits.availableTitle')}</CardTitle>
            <CardDescription>{t('customer.billing.credits.availableDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {balanceQuery.isLoading ? (
              <Skeleton className="h-8 w-32" />
            ) : (
              <p className="text-2xl font-semibold">
                {formatCredits(balanceQuery.data?.availableCredits, language)}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('customer.billing.credits.expiringTitle', { days: expiringDays })}</CardTitle>
            <CardDescription>{t('customer.billing.credits.expiringDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {expiringQuery.isLoading ? (
              <Skeleton className="h-8 w-32" />
            ) : (
              <>
                <p className="text-2xl font-semibold">
                  {formatCredits(expiringCredits, language)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('customer.billing.credits.expiringCount', { count: expiringRecords.length })}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('customer.billing.credits.expiringTableTitle')}</CardTitle>
          <CardDescription>{t('customer.billing.credits.expiringTableDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('customer.billing.credits.table.package')}</TableHead>
                <TableHead>{t('customer.billing.credits.table.remaining')}</TableHead>
                <TableHead>{t('customer.billing.credits.table.expiresAt')}</TableHead>
                <TableHead>{t('customer.billing.credits.table.daysUntilExpiry')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{renderExpiringRows()}</TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex items-center justify-between">
          <div>
            <CardTitle>{t('customer.billing.credits.historyTitle')}</CardTitle>
            <CardDescription>{t('customer.billing.credits.historyDescription')}</CardDescription>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={retryAll}
            disabled={isLoading}
            aria-label={t('customer.billing.credits.retry')}
          >
            <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('customer.billing.credits.table.package')}</TableHead>
                <TableHead>{t('customer.billing.credits.table.amount')}</TableHead>
                <TableHead>{t('customer.billing.credits.table.purchased')}</TableHead>
                <TableHead>{t('customer.billing.credits.table.remaining')}</TableHead>
                <TableHead>{t('customer.billing.credits.table.status')}</TableHead>
                <TableHead>{t('customer.billing.credits.table.purchasedAt')}</TableHead>
                <TableHead>{t('customer.billing.credits.table.expiresAt')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{renderHistoryRows()}</TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
